import {
  useReducer,
  createContext,
  useCallback,
  useEffect,
  useState,
  useContext,
} from 'react';
import { generateDateMonthsPrevious } from 'components/Dashboard/Dashboard.context.helpers';
import { useAmplitude } from 'hooks/useAmplitude';
import { useLocale } from 'hooks/useLocale';
import { useGetApplications } from 'lendingApi/applications.hooks';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';
import reducer from './ApplicationTable.reducer';
import { TApplicationTableState } from './ApplicationTable.types';
import { LOCAL_STORAGE_KEY } from './applicationTableHeader/searchBar/RecentSearches';
import {
  getDefaultDateRangeNumber,
  getDefaultIncludePartnerChannel,
} from './applicationUtils';

const ApplicationTableContext = createContext<
  | {
      state: TApplicationTableState;
      searchApplication: (searchString?: string) => void;
      searchApplicationDateRange: (dateFrom: string | null) => void;
      searchApplicationPartnerChannelToggle: (
        includePartnerChannel: boolean,
      ) => void;
      applicationsRefetch: () => void;
      getApplicationLoading: () => boolean;
      setRedirectionId: (id?: string) => void;
      isLoadingApplications?: boolean;
      redirectionId?: string;
    }
  | undefined
>(undefined);

export const ApplicationTableProvider = (props: any) => {
  const { trackEvent } = useAmplitude();
  const initialState = {
    error: null,
    applicationProductTypes: [],
    dateFrom: generateDateMonthsPrevious(getDefaultDateRangeNumber()), // Fetch from the API
    includePartnerChannel: getDefaultIncludePartnerChannel(),
    statusList: [],
  };

  const [redirectionId, setRedirectionId] = useState<string>();
  const [state, dispatch] = useReducer(reducer, initialState);
  const { locale } = useLocale();
  const {
    data: applications,
    isError: isApplicationError,
    isLoading: isApplicationLoading,
    refetch: applicationsRefetch,
    status,
  } = useGetApplications(
    state.searchString,
    state.dateFrom,
    state.includePartnerChannel,
  );
  const searchApplication = useCallback(async (searchString?: string) => {
    const currentSearches = await localStorage.getItem(LOCAL_STORAGE_KEY);
    const currentSearchesParsed =
      currentSearches && (await JSON.parse(currentSearches));
    if (currentSearchesParsed) {
      localStorage.setItem(
        LOCAL_STORAGE_KEY,
        JSON.stringify([...currentSearchesParsed, searchString]),
      );
    } else {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify([searchString]));
    }
    await dispatch({ type: 'searchApplication', searchString });
  }, []);

  async function searchApplicationDateRange(dateFrom: string | null) {
    await dispatch({ type: 'searchApplicationDateRange', dateFrom });
  }

  async function searchApplicationPartnerChannelToggle(
    includePartnerChannel: boolean,
  ) {
    await dispatch({
      type: 'searchApplicationPartnerChannelToggle',
      includePartnerChannel,
    });
  }

  // TODO: remove after new dashboard release https://iwoca.atlassian.net/browse/BAE-3088
  function getApplicationLoading(): boolean {
    return isApplicationLoading || status === 'pending';
  }

  const isLoading = isApplicationLoading || status === 'pending';

  const getData = useCallback(() => {
    if (isApplicationError) {
      dispatch({
        type: 'error',
        error: isApplicationError,
      });
    }

    dispatch({
      type: 'fetchedApplications',
      applications,
      locale,
    });

    if (!isApplicationError && state.searchString && applications) {
      trackEvent(AMPLITUDE_EVENTS.searched, {
        Length: state.searchString.length,
        'Number of results': applications.data?.length,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isApplicationError, applications, locale, state.searchString]);

  useEffect(() => {
    async function getApplicationTableData() {
      await getData();
    }

    getApplicationTableData();
  }, [getData]);

  return (
    <ApplicationTableContext.Provider
      value={{
        state,
        isLoading,
        searchApplication,
        searchApplicationDateRange,
        searchApplicationPartnerChannelToggle,
        applicationsRefetch,
        getApplicationLoading,
        setRedirectionId,
        redirectionId,
      }}
      {...props}
    />
  );
};

export function useApplicationTable() {
  const context = useContext(ApplicationTableContext);
  if (context === undefined) {
    throw new Error('useApplicationTable must be used within');
  }
  return context;
}
